import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "./AuthContext";
import ReactMarkdown from "react-markdown";
import "./AISearch.css";

const AISearch = () => {
  const { user } = useContext(AuthContext);
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState(null);
  const [relevantProfiles, setRelevantProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [aiStatus, setAIStatus] = useState({ available: false, checking: true });
  const [searchHistory, setSearchHistory] = useState([]);
  const [selectedUniversity, setSelectedUniversity] = useState("berkeley");
  const [yearFrom, setYearFrom] = useState("");
  const [yearTo, setYearTo] = useState("");
  
  const currentYear = new Date().getFullYear();
  
  const universities = {
    berkeley: { name: "UC Berkeley", emoji: "🐻", color: "#003262" },
    imperial: { name: "Imperial College London", emoji: "🎓", color: "#003E74" },
    anu: { name: "Australian National University", emoji: "🦘", color: "#BE830E" },
    wits: { name: "University of the Witwatersrand", emoji: "🇿🇦", color: "#003B5C" },
    ubc: { name: "University of British Columbia", emoji: "🍁", color: "#002145" }
  };

  // Check AI status on mount and when university changes
  useEffect(() => {
    checkAIStatus();
  }, [selectedUniversity]);

  const checkAIStatus = async () => {
    try {
      const response = await fetch(`/api/ai-search/status?university=${selectedUniversity}`, {
        credentials: "include",
      });
      const data = await response.json();
      setAIStatus({ available: data.available, checking: false, message: data.message });
    } catch (err) {
      setAIStatus({ available: false, checking: false, message: "Unable to connect to AI service" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;

    setLoading(true);
    setError(null);
    setAnswer(null);
    setRelevantProfiles([]);

    try {
      const response = await fetch(`/api/ai-search/ask`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ 
          question: question.trim(),
          university: selectedUniversity,
          yearFrom: yearFrom || undefined,
          yearTo: yearTo || undefined
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to get answer");
      }

      setAnswer(data.answer);
      setRelevantProfiles(data.relevantProfiles || []);
      
      // Add to search history
      setSearchHistory(prev => [
        { question: question.trim(), answer: data.answer, timestamp: new Date(), university: selectedUniversity },
        ...prev.slice(0, 4)
      ]);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuestion(suggestion);
  };

  const suggestionsByUniversity = {
    berkeley: [
      "What papers have been published about machine learning at UC Berkeley?",
      "Tell me about researchers working on robotics and their publications",
      "Who are the experts in artificial intelligence and what are their recent papers?",
      "Describe the research focus of Computer Science faculty members",
      "What climate change research is happening at UC Berkeley?",
      "Who has expertise in computational biology and what have they published?",
    ],
    imperial: [
      "What papers have been published about machine learning at Imperial?",
      "Tell me about researchers working on biomedical engineering",
      "Who are the experts in artificial intelligence at Imperial College?",
      "Describe the research focus of Engineering faculty members",
      "What climate change research is happening at Imperial College London?",
      "Who has expertise in materials science and what have they published?",
    ],
    anu: [
      "What research is happening in astrophysics at ANU?",
      "Tell me about climate science researchers at Australian National University",
      "Who are the experts in quantum computing at ANU?",
      "What papers have been published about renewable energy at ANU?",
      "Who works on machine learning and data science at ANU?",
      "Describe the research focus of environmental science faculty at ANU",
    ],
    wits: [
      "What research is happening in mining engineering at Wits?",
      "Tell me about public health researchers at University of the Witwatersrand",
      "Who are the experts in palaeontology at Wits?",
      "What papers have been published about HIV/AIDS research at Wits?",
      "Who works on computational science at Wits?",
      "Describe the research focus of physics faculty at Wits",
    ],
    ubc: [
      "What papers have been published about forestry and environmental science at UBC?",
      "Tell me about researchers working on clean energy at UBC",
      "Who are the experts in artificial intelligence at UBC?",
      "What climate change research is happening at University of British Columbia?",
      "Who has expertise in biomedical research and what have they published?",
      "Describe the research focus of Computer Science faculty at UBC",
    ]
  };
  
  const suggestions = suggestionsByUniversity[selectedUniversity] || suggestionsByUniversity.berkeley;

  if (!user) {
    return (
      <div className="ai-search-container">
        <div className="login-prompt">
          <h2>🔐 Login Required</h2>
          <p>Please log in to use AI-powered search.</p>
        </div>
      </div>
    );
  }

  const currentUni = universities[selectedUniversity];

  return (
    <div className="ai-search-container">
      <div className="ai-search-header" style={{ borderColor: currentUni.color }}>
        <a href="/dashboard" className="back-link">← Back to Dashboard</a>
        <h1>🤖 AI-Powered Research Assistant</h1>
        <p className="subtitle">Ask questions about {currentUni.name} researchers in natural language</p>
        
        {/* University Selector */}
        <div className="university-selector">
          {Object.entries(universities).map(([key, uni]) => (
            <button
              key={key}
              className={`uni-btn ${selectedUniversity === key ? 'active' : ''}`}
              onClick={() => {
                setSelectedUniversity(key);
                setAnswer(null);
                setRelevantProfiles([]);
                setError(null);
                setYearFrom("");
                setYearTo("");
              }}
              style={{
                '--uni-color': uni.color,
                backgroundColor: selectedUniversity === key ? uni.color : 'transparent',
                color: selectedUniversity === key ? 'white' : uni.color,
                borderColor: uni.color
              }}
            >
              {uni.emoji} {uni.name}
            </button>
          ))}
        </div>
        
        {/* AI Status Badge */}
        <div className={`ai-status ${aiStatus.available ? 'available' : 'limited'}`}>
          {aiStatus.checking ? (
            <span>🔄 Checking AI status...</span>
          ) : aiStatus.available ? (
            <span>✅ AI-Powered (GPT-4o-mini + RAG) - {currentUni.name}</span>
          ) : (
            <span>⚡ Keyword Search Mode (Add OPENAI_API_KEY for full AI)</span>
          )}
        </div>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSubmit} className="ai-search-form">
        <div className="search-input-wrapper">
          <textarea
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask a question about researchers... e.g., 'Who works on machine learning?' or 'Find experts in renewable energy'"
            rows={3}
            disabled={loading}
          />
          <button type="submit" disabled={loading || !question.trim()}>
            {loading ? (
              <>🔄 Searching...</>
            ) : (
              <>🔍 Ask</>
            )}
          </button>
        </div>
        
        {/* Year Filter */}
        <div className="ai-year-filter">
          <span className="ai-year-label">📅 Publication year:</span>
          <div className="ai-year-inputs">
            <input
              type="number"
              value={yearFrom}
              onChange={(e) => setYearFrom(e.target.value)}
              placeholder="From"
              min="1950"
              max={currentYear}
              className="ai-year-input"
            />
            <span className="ai-year-sep">—</span>
            <input
              type="number"
              value={yearTo}
              onChange={(e) => setYearTo(e.target.value)}
              placeholder="To"
              min="1950"
              max={currentYear}
              className="ai-year-input"
            />
          </div>
          <div className="ai-year-presets">
            <button type="button" onClick={() => { setYearFrom(String(currentYear)); setYearTo(String(currentYear)); }}>
              {currentYear}
            </button>
            <button type="button" onClick={() => { setYearFrom(String(currentYear - 2)); setYearTo(String(currentYear)); }}>
              Last 3 yrs
            </button>
            <button type="button" onClick={() => { setYearFrom(String(currentYear - 4)); setYearTo(String(currentYear)); }}>
              Last 5 yrs
            </button>
            {(yearFrom || yearTo) && (
              <button type="button" className="ai-year-clear" onClick={() => { setYearFrom(""); setYearTo(""); }}>
                ✕ All years
              </button>
            )}
          </div>
        </div>
      </form>

      {/* Suggestions */}
      {!answer && !loading && (
        <div className="suggestions">
          <p className="suggestions-label">💡 Try asking:</p>
          <div className="suggestion-chips">
            {suggestions.map((suggestion, i) => (
              <button
                key={i}
                className="suggestion-chip"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="ai-error">
          <span>❌ {error}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="ai-loading">
          <div className="loading-spinner"></div>
          <p>Searching through {currentUni.name} researcher profiles...</p>
          <p className="loading-detail">Using semantic search to find relevant experts</p>
        </div>
      )}

      {/* Answer */}
      {answer && !loading && (
        <div className="ai-answer-section">
          <div className="ai-answer">
            <h3>🎯 AI Research Assistant Answer</h3>
            <div className="answer-content markdown-content">
              <ReactMarkdown
                components={{
                  // Style links
                  a: ({node, ...props}) => (
                    <a {...props} target="_blank" rel="noopener noreferrer" />
                  ),
                  // Style bold text
                  strong: ({node, ...props}) => (
                    <strong className="highlight" {...props} />
                  ),
                  // Style lists
                  ul: ({node, ...props}) => (
                    <ul className="answer-list" {...props} />
                  ),
                  li: ({node, ...props}) => (
                    <li className="answer-list-item" {...props} />
                  )
                }}
              >
                {answer}
              </ReactMarkdown>
            </div>
          </div>

          {/* Relevant Profiles */}
          {relevantProfiles.length > 0 && (
            <div className="relevant-profiles">
              <h3>👥 Relevant Researchers ({relevantProfiles.length})</h3>
              <div className="profiles-grid">
                {relevantProfiles.map((profile, i) => (
                  <div key={profile.orcidId || i} className="profile-card">
                    <div className="profile-header">
                      <h4>{profile.fullName || profile.name}</h4>
                      {profile.relevanceScore && (
                        <span className="relevance-badge">
                          {(profile.relevanceScore * 100).toFixed(0)}% match
                        </span>
                      )}
                    </div>
                    
                    {profile.orcidId && (
                      <p className="profile-orcid">
                        <a 
                          href={`https://orcid.org/${profile.orcidId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          🆔 {profile.orcidId}
                        </a>
                      </p>
                    )}
                    
                    {profile.biography && (
                      <p className="profile-bio">
                        {profile.biography.length > 150 
                          ? `${profile.biography.substring(0, 150)}...` 
                          : profile.biography}
                      </p>
                    )}
                    
                    {profile.works && profile.works.length > 0 && (
                      <div className="profile-works">
                        <strong>Recent work:</strong>
                        <ul>
                          {profile.works.slice(0, 2).map((work, j) => (
                            <li key={j}>
                              {work.title}
                              {work.year && <span> ({work.year})</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && !loading && (
        <div className="search-history">
          <h3>📜 Recent Questions</h3>
          <ul>
            {searchHistory.map((item, i) => (
              <li key={i}>
                <button 
                  className="history-item"
                  onClick={() => setQuestion(item.question)}
                >
                  <span className="history-question">{item.question}</span>
                  <span className="history-time">
                    {new Date(item.timestamp).toLocaleTimeString()}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

     
      
        <div className="example-questions">
          <p><strong>💡 Pro tip:</strong> Ask detailed questions like:</p>
          <ul>
            <li>"What papers have been published about X?"</li>
            <li>"Tell me about the research focus of Y"</li>
            <li>"Who are the experts in Z and what have they published?"</li>
          </ul>
        </div>
      </div>
  );
};

export default AISearch;
